import { createContext } from 'react';

import type { SliderProps } from '@change/ds-forms/Slider';

import type { PaymentType } from '#src/app/shared/utils/payments';

import type { PromoteFormFeatureConfigFieldsFragment } from '../shared/api/promoteFormFeatureConfigFields.graphql';
import type { PromoteFormViewerFieldsFragment } from '../shared/api/promoteFormViewerFields.graphql';

type SavedPaymentMethodData = NonNullable<
	NonNullable<NonNullable<PromoteFormViewerFieldsFragment['user']>['authOnlyData']>['paymentMethods']
>[0];

export type SavedPaymentMethod =
	| (Exclude<SavedPaymentMethodData, { __typename: 'PaymentMethodOther' }> & {
			type: PaymentType;
	  })
	| (Extract<SavedPaymentMethodData, { __typename: 'PaymentMethodOther' }> & {
			type: undefined;
	  });

export type PromoteFormData = {
	petition: {
		id: string;
		slug: string;
		ask: string;
		displayTitle: string;
		categoryTags: string[];
		geoReach: string | null;
		relevantLocationLocalizedName: string | null;
		feeds: {
			heroFeed: Array<{
				userId: string;
				userDisplayName: string;
				userPhoto: string | null;
				event:
					| {
							__typename: 'HeroFeedPromotionEvent';
							id: string;
							createdAt: string;
							amountUsd: number;
							amount: number;
							amountLocalizedWithCurrency: string | null;
					  }
					| {
							__typename: 'HeroFeedShareEvent';
							id: string;
							createdAt: string;
					  };
			}>;
		};
		user: {
			id: string;
		};
	};
	promotionCountrySettings: {
		amountChoices: Array<{ amount: number; localizedAmount: string }>;
		anchorAmount?: { amount: number; localizedAmount: string } | null;
		minimumAmount?: { amount: number; localizedAmount: string } | null;
		maximumAmount?: { amount: number; localizedAmount: string } | null;
		confirmationAmount?: { amount: number; localizedAmount: string } | null;
	};
	promotionSettings: {
		cpm: number;
		promotionToken: string;
		bonusCpm?: number | null;
		bonusThreshold?: number | null;
	};
	currency: {
		code: string;
		exchangeRate: number;
	};
	featureConfigs: Omit<PromoteFormFeatureConfigFieldsFragment, 'subscriptionConfiguration'>;
	subscriptionConfiguration: {
		amounts: number[];
		defaultAmount: number;
		minAmount: number;
		maxAmount: number;
		minSliderAmount?: number;
		maxSliderAmount?: number;
		sliderStep?: number;
		sliderLayout?: SliderProps['layout'];
	} | null;
	user: {
		id: string;
		email: string;
		name: string;
		firstName: string;
		lastName: string;
		hasPaymentMethod: boolean;
		hasSubscription: boolean;
		savedPaymentMethods: SavedPaymentMethod[];
	};
	amountChoices: Array<{ amount: number; localizedAmount: string }>;
	membershipProgram: { readonly id: string; readonly slug: string | null } | null;
	guestUserData: { firstName: string; lastName: string; email: string } | null;
	countryCode: string;
};

export const PromoteFormDataContext = createContext<PromoteFormData | null>(null);
